/*
 * The price list and the auto quoter (copied from the WordPress Steward's includes/prices.php, steward_price_home).
 *
 * The owner types the list once (Settings, Price list). A quote is then priced from a few answers: the service, the home's
 * size, how often and the add-ons. The math:
 * - A home is priced by the room or by the square foot, never below the service's minimum. Rooms are bedrooms plus
 *   bathrooms (a half bath counts as one) plus the rooms every home has (baseRooms, kitchen and living area) plus others.
 *   In square foot mode a rate with no square foot price is priced by the room.
 * - The first visit is the service at its own rates, plus add-ons. Add-ons are on the first visit only.
 * - When the service can repeat, every visit after the first is priced at the repeat schedule's own rates.
 * Rates are cents: a room price in whole cents, a square foot price in cents with up to 2 places (8.5 is 8.5 cents a sq ft).
 * Each answer is rounded to whole cents.
 */
import { z } from "zod";

export const PRICE_OFTEN = ["once", "weekly", "biweekly", "monthly"] as const;
export type PriceOften = (typeof PRICE_OFTEN)[number];
export type RepeatOften = Exclude<PriceOften, "once">;

/** How often, in words. The job's own frequency names match the keys (once is one-time). */
export const PRICE_OFTEN_WORDS: Record<PriceOften, string> = {
  once: "One time",
  weekly: "Every week",
  biweekly: "Every two weeks",
  monthly: "Once a month",
};
/** The job frequency a quote gets for each answer. */
export const OFTEN_FREQUENCY: Record<PriceOften, string> = {
  once: "one-time",
  weekly: "weekly",
  biweekly: "biweekly",
  monthly: "monthly",
};

const id = z.string().trim().min(1).max(20);
export const priceRate = z.object({
  perRoomCents: z.number().int().min(0).max(1_000_000).nullable(),
  perSqftCents: z
    .number()
    .min(0)
    .max(10_000)
    .refine((n) => Math.round(n * 100) === n * 100, "Use at most 2 places after the point.")
    .nullable(),
  minCents: z.number().int().min(0).max(10_000_000),
});
export type PriceRate = z.infer<typeof priceRate>;

/** A page about a service or add-on ("See what's included"): http or https only, or empty for none. */
export const infoLink = z.union([
  z.literal(""),
  z.url({ protocol: /^https?$/, error: "Use a web address that starts with https://" }).max(300),
]);

/** A line's link as typed or copied: empty means none. */
export const lineLink = infoLink
  .transform((v) => v || null)
  .nullable()
  .optional();

/** What a service or add-on covers and where to read more. Both go on the lines the auto quote makes. */
const aboutFields = {
  /** A line or two on what it covers. Shows under the line on quotes, jobs, visits and invoices. */
  about: z.string().trim().max(300).optional(),
  link: infoLink.optional(),
};

export const priceService = priceRate.extend({
  id,
  name: z.string().trim().min(1, "Name the service.").max(60),
  /** May start a repeating schedule (move out cleaning usually does not). */
  repeats: z.boolean(),
  ...aboutFields,
});
export type PriceService = z.infer<typeof priceService>;

export const priceExtra = z.object({
  id,
  name: z.string().trim().min(1, "Name the add-on.").max(60),
  priceCents: z.number().int().min(0).max(1_000_000),
  /**
   * The flat price on a repeating plan (owner, 2026-11-02: inside the oven $40, $25 for plan clients, the same for weekly,
   * every 2 weeks and monthly). Null or left out: plan clients pay the usual price.
   */
  planCents: z.number().int().min(0).max(1_000_000).nullable().optional(),
  /** Priced each (2 fridges), else just yes or no. */
  each: z.boolean(),
  /** Offered with this service only; null for every service. */
  only: id.nullable(),
  ...aboutFields,
});
export type PriceExtra = z.infer<typeof priceExtra>;

export const priceList = z.object({
  baseRooms: z.number().int().min(0).max(10),
  services: z.array(priceService).max(30),
  repeat: z.object({ weekly: priceRate, biweekly: priceRate, monthly: priceRate }),
  extras: z.array(priceExtra).max(60),
  /** Maid service by the hour: one cleaner's hour. Empty means HOURLY_CENTS. */
  hourlyCents: z.number().int().min(0).max(100_000).optional(),
});
export type PriceList = z.infer<typeof priceList>;

const NO_RATE: PriceRate = { perRoomCents: null, perSqftCents: null, minCents: 0 };
/** An empty list: the quoter asks the owner to fill it in first. */
export const EMPTY_PRICE_LIST: PriceList = {
  baseRooms: 2,
  services: [],
  repeat: { weekly: NO_RATE, biweekly: NO_RATE, monthly: NO_RATE },
  extras: [],
};

/** A repeat schedule is offered when it has any price at all. */
export const rateOffered = (r: PriceRate) => r.perRoomCents !== null || r.perSqftCents !== null || r.minCents > 0;

export interface HomeAnswers {
  serviceId: string;
  sizeBy: "rooms" | "sqft";
  beds: number;
  baths: number;
  /** Rooms beyond bedrooms, bathrooms and the base rooms (an office, a game room). */
  other: number;
  sqft: number;
  often: PriceOften;
  /** Add-on id to true, or to a count for one priced each. */
  extras: Record<string, boolean | number>;
  /** The office kept the price after the clean showed a bigger home (Let it pass); plans price from the real size. */
  letPass?: boolean | undefined;
}

export interface HomePrice {
  service: PriceService;
  rooms: number;
  /** The square feet priced by, or 0 when priced by the room. */
  sqft: number;
  /** The service itself on the first visit. */
  baseCents: number;
  /** The base is the service's minimum. */
  isMin: boolean;
  /** listCents: the usual price, when a plan price was used instead. */
  extras: { id: string; label: string; cents: number; listCents: number | null; about: string | null; link: string | null }[];
  /** The first visit in all: base plus add-ons. 0 when the size is missing. */
  firstVisitCents: number;
  /** Every visit after the first, when the service repeats and the schedule is offered. */
  repeat: { often: RepeatOften; label: string; perVisitCents: number } | null;
}

/** A service's or add-on's words and link for a line, null when not set. */
export const aboutOf = (x: { about?: string | undefined; link?: string | undefined }) => ({
  about: x.about?.trim() ? x.about : null,
  link: x.link?.trim() ? x.link : null,
});

const whole = (n: number) => (Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0);

/** The price of a home clean from the list, or null when the service is not on it. */
export function priceHome(list: PriceList, a: HomeAnswers): HomePrice | null {
  const service = list.services.find((s) => s.id === a.serviceId);
  if (!service) return null;
  const bySqft = a.sizeBy === "sqft" && service.perSqftCents !== null;
  const sqft = whole(a.sqft);
  const baths = Number.isFinite(a.baths) ? Math.max(0, a.baths) : 0;
  const rooms = whole(a.beds) + Math.ceil(baths) + list.baseRooms + whole(a.other);
  const calc = (r: PriceRate) => {
    if (bySqft && r.perSqftCents !== null) return sqft ? Math.max(r.minCents, Math.round(r.perSqftCents * sqft)) : 0;
    return Math.max(r.minCents, Math.round((r.perRoomCents ?? 0) * rooms));
  };
  const baseCents = calc(service);
  const onPlan = a.often !== "once" && service.repeats && rateOffered(list.repeat[a.often]);
  const extras: HomePrice["extras"] = [];
  for (const e of list.extras) {
    if (e.only !== null && e.only !== service.id) continue;
    const pick = a.extras[e.id];
    const each = extraCents(e, onPlan);
    const was = each === e.priceCents ? null : e.priceCents;
    if (e.each) {
      const count = typeof pick === "number" ? whole(pick) : pick === true ? 1 : 0;
      if (count > 0)
        extras.push({
          id: e.id,
          label: `${e.name} × ${String(count)}`,
          cents: each * count,
          listCents: was === null ? null : was * count,
          ...aboutOf(e),
        });
    } else if (pick) extras.push({ id: e.id, label: e.name, cents: each, listCents: was, ...aboutOf(e) });
  }
  const firstVisitCents = baseCents ? baseCents + extras.reduce((s, x) => s + x.cents, 0) : 0;
  let repeat: HomePrice["repeat"] = null;
  if (a.often !== "once" && service.repeats && rateOffered(list.repeat[a.often]))
    repeat = { often: a.often, label: PRICE_OFTEN_WORDS[a.often], perVisitCents: calc(list.repeat[a.often]) };
  return {
    service,
    rooms,
    sqft: bySqft ? sqft : 0,
    baseCents,
    isMin: baseCents > 0 && baseCents === service.minCents,
    extras,
    firstVisitCents,
    repeat,
  };
}

/** An add-on's price for one: its plan price on a repeating plan when it has one, else the usual price. */
export function extraCents(e: Pick<PriceExtra, "priceCents" | "planCents">, onPlan: boolean): number {
  return onPlan && e.planCents != null ? e.planCents : e.priceCents;
}

/** "3 bed, 2 bath" or "1,800 sq ft". */
export function homeSize(a: Pick<HomeAnswers, "beds" | "baths" | "other">, p: Pick<HomePrice, "sqft">): string {
  if (p.sqft) return `${p.sqft.toLocaleString("en-US")} sq ft`;
  const parts = [`${String(whole(a.beds))} bed`, `${String(Math.max(0, a.baths))} bath`];
  if (whole(a.other)) parts.push(`${String(whole(a.other))} more ${whole(a.other) === 1 ? "room" : "rooms"}`);
  return parts.join(", ");
}

export interface QuickQuote {
  title: string;
  /** The quote's (and later the job's) frequency. */
  frequency: string;
  /** note: what the line covers (the price list's words). link: a page that lists what is included. */
  lines: { description: string; unitPriceCents: number; recurring: boolean; note?: string | null; link?: string | null }[];
}

/**
 * What the first visit costs. Starting a plan (planStart, the client had a standard, deep or move clean in the last
 * PLAN_READY_DAYS days) the first visit is a plan visit: the repeat price plus add-ons, no first visit price on top.
 */
export function firstVisitOf(p: HomePrice, planStart = false): number {
  if (!planStart || !p.repeat) return p.firstVisitCents;
  return p.repeat.perVisitCents + p.extras.reduce((s, x) => s + x.cents, 0);
}

/**
 * The quote lines for a price. One time: the service and the add-ons. Repeating: the repeat price on every visit, the
 * difference up to the service's own price as a first visit line (so the first visit costs what the list says), and the
 * add-ons on the first visit. Starting a plan (planStart) leaves the first visit line off. Null when there is nothing to
 * price yet. Each line carries the price list's words and link.
 */
export function quickQuoteLines(a: HomeAnswers, p: HomePrice, planStart = false): QuickQuote | null {
  if (!p.firstVisitCents) return null;
  const size = homeSize(a, p);
  const service = aboutOf(p.service);
  const words = { note: service.about, link: service.link };
  const extras = p.extras.map((x) => ({
    description: x.label,
    unitPriceCents: x.cents,
    recurring: false,
    note: x.about,
    link: x.link,
  }));
  if (!p.repeat)
    return {
      title: p.service.name,
      frequency: OFTEN_FREQUENCY.once,
      lines: [{ description: `${p.service.name}, ${size}`, unitPriceCents: p.baseCents, recurring: false, ...words }, ...extras],
    };
  const upgrade = planStart ? 0 : p.baseCents - p.repeat.perVisitCents;
  // The service's words go on its first visit line, or on the repeat line when the first visit costs the same.
  return {
    title: planStart ? `Cleaning ${p.repeat.label.toLowerCase()}` : `${p.service.name}, then ${p.repeat.label.toLowerCase()}`,
    frequency: OFTEN_FREQUENCY[p.repeat.often],
    lines: [
      {
        description: `Cleaning ${p.repeat.label.toLowerCase()}, ${size}`,
        unitPriceCents: p.repeat.perVisitCents,
        recurring: true,
        ...(upgrade > 0 ? { note: null, link: null } : words),
      },
      ...(upgrade > 0
        ? [{ description: `${p.service.name} on the first visit`, unitPriceCents: upgrade, recurring: false, ...words }]
        : []),
      ...extras,
    ],
  };
}

/**
 * What went into a line's price, in words for the crew and the office: "3 bed, 2 bath, 2 more rooms", "1,200 sq ft" or
 * "2,400 sq ft of grass". From the answers the line (or its quote) was priced from; null when there are none.
 */
export function pricedFor(raw: unknown): string | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
  if (r.kind === "lawn") return n(r.sqft) > 0 ? `${n(r.sqft).toLocaleString("en-US")} sq ft of grass` : null;
  if (typeof r.serviceId !== "string") return null;
  if (r.sizeBy === "sqft" && n(r.sqft) > 0) return `${whole(n(r.sqft)).toLocaleString("en-US")} sq ft`;
  if (n(r.beds) + n(r.baths) + n(r.other) <= 0) return null;
  return homeSize({ beds: n(r.beds), baths: n(r.baths), other: n(r.other) }, { sqft: 0 });
}

/** "$40" or "$7.50": a rate the way the price list reads. */
const rateMoney = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-US", Number.isInteger(cents / 100) ? {} : { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export interface PriceMath {
  /** "3 bed, 2 bath, 1 more room" or "1,800 sq ft". */
  size: string;
  /** "7 rooms, with the kitchen and living room, × $40" or "1,800 sq ft × 8.5¢", or empty when the lowest price won. */
  math: string;
  /** The service (or repeat schedule) costs its lowest price for a home this size. */
  isMin: boolean;
  /** What the math comes to: the first visit's service (no add-ons) or, for a repeat, each visit after the first. */
  cents: number;
  /** The repeat schedule it is for, or null for the first visit. */
  repeat: RepeatOften | null;
  /** The repeat prices this home would get, for a one time price. */
  repeats: { often: RepeatOften; label: string; perVisitCents: number }[];
}

/**
 * How a job line's price came from the list, in words for the line on the job page (owner, 2026-10-29: "on a line item
 * that used autoquote for it, to underneath show how it got that"). Null when the service is gone or the size is missing.
 */
export function priceMath(list: PriceList, a: HomeAnswers): PriceMath | null {
  const p = priceHome(list, a);
  if (!p?.baseCents) return null;
  const repeat = p.repeat ? p.repeat.often : null;
  const rate = repeat ? list.repeat[repeat] : p.service;
  const cents = p.repeat ? p.repeat.perVisitCents : p.baseCents;
  const bySqft = p.sqft > 0 && rate.perSqftCents !== null;
  const isMin = cents === rate.minCents;
  const math = isMin
    ? ""
    : bySqft
      ? `${p.sqft.toLocaleString("en-US")} sq ft × ${String(rate.perSqftCents)}¢`
      : `${String(p.rooms)} rooms, with the kitchen and living room, × ${rateMoney(rate.perRoomCents ?? 0)}`;
  const repeats = repeat
    ? []
    : (["weekly", "biweekly", "monthly"] as const)
        .map((often) => priceHome(list, { ...a, often })?.repeat ?? null)
        .filter((r): r is NonNullable<HomePrice["repeat"]> => r !== null);
  return { size: homeSize(a, p), math, isMin, cents, repeat, repeats };
}

/** Maid service by the hour, when the price list has no rate of its own: $50 a cleaner an hour (owner, 2026-10-30). */
export const HOURLY_CENTS = 5000;

/** Maid service by the hour can also come every day (owner, 2026-11-02: "2 hours every day or week or two weeks"). */
export const HOURLY_OFTEN = ["once", "daily", "weekly", "biweekly", "monthly"] as const;
export type HourlyOften = (typeof HOURLY_OFTEN)[number];
export const HOURLY_OFTEN_WORDS: Record<HourlyOften, string> = { ...PRICE_OFTEN_WORDS, daily: "Every day" };

export interface HourlyAnswers {
  cleaners: number;
  hours: number;
  often: HourlyOften;
}

/** Maid service by the hour on a quote (My Trades Get a quote, and the quote page that changes it). */
export const hourlyAnswersShape = z
  .object({
    kind: z.literal("hourly"),
    cleaners: z.number().int().min(1).max(6),
    hours: z
      .number()
      .min(1)
      .max(10)
      .refine((h) => Math.round(h * 4) === h * 4, "Use quarter hours."),
    often: z.enum(HOURLY_OFTEN),
  })
  .strict();

/**
 * Maid service by the hour: cleaners × hours × one cleaner's hour, the same price every visit. Hours go to the quarter
 * hour. Null when the cleaners or hours are missing.
 */
export function hourlyQuoteLines(list: PriceList, a: HourlyAnswers): QuickQuote | null {
  const cleaners = whole(a.cleaners);
  const hours = Number.isFinite(a.hours) ? Math.max(0, Math.round(a.hours * 4) / 4) : 0;
  const rate = list.hourlyCents ?? HOURLY_CENTS;
  const cents = Math.round(cleaners * hours * rate);
  if (!cents) return null;
  const who = `${String(cleaners)} ${cleaners === 1 ? "cleaner" : "cleaners"}`;
  const long = `${String(hours)} ${hours === 1 ? "hour" : "hours"}`;
  return {
    title: a.often === "once" ? "Maid service" : `Maid service, ${HOURLY_OFTEN_WORDS[a.often].toLowerCase()}`,
    frequency: a.often === "daily" ? "daily" : OFTEN_FREQUENCY[a.often],
    lines: [
      {
        description: `Maid service, ${who} for ${long} at ${rateMoney(rate)} an hour each`,
        unitPriceCents: cents,
        recurring: a.often !== "once",
      },
    ],
  };
}
